import { AuthActionButton } from '@/features/auth/AuthActionButton'
import { useAuthContext } from '@/features/auth/model/auth-context'
import { Button } from '@/shared/ui'
import { AppIcon } from '@/shared/ui/icons'
import { TermsUseAndPolicies } from '@/shared/ui/TermsUseAndPolicies'

type AuthPageFooterProps = {
  pageAction?: 'sign-in' | 'sign-up' | 'sub-route'
}

/**
 * Rodapé das telas de autenticação, contextual à rota.
 *
 * | Rota | Consentimento | Caminho de saída |
 * |---|---|---|
 * | acesso | ao entrar | "Cadastre-se" |
 * | cadastro | ao cadastrar-se | "Já possui conta? Entrar" |
 * | recuperação e confirmação | ausente | "Voltar para o login" |
 *
 * O consentimento aparece só onde a conta é criada ou acessada: numa tela de
 * redefinir senha ele já ocorreu, e o texto ainda dizia "Ao entrar, você
 * concorda…" — factualmente errado ali. O divisor com o escudo acompanha a
 * frase; sozinho, era um símbolo sem texto.
 *
 * E oferecer "Cadastre-se" a quem está redefinindo a própria senha é sugerir a
 * ação errada no momento errado.
 */
export function AuthPageFooter({ pageAction = 'sign-in' }: AuthPageFooterProps) {
  const { goToSignIn, goToSignUp } = useAuthContext()
  const mostraConsentimento =
    pageAction === 'sign-in' || pageAction === 'sign-up'

  if (!mostraConsentimento) {
    return (
      <footer className="flex w-full flex-col items-center gap-3 short:gap-2">
        {/* `goToSignIn` preserva o `callbackUrl` da URL corrente, então quem
            veio de uma rota protegida volta para ela depois de entrar. */}
        <Button
          type="button"
          variant="link"
          // `min-h` porque a variante `link` não tem altura própria, e o
          // alvo de toque em auth é 44px (ACCESSIBILITY.md §3).
          className="!h-auto min-h-11 p-0 text-sm font-semibold"
          onClick={() => goToSignIn()}
          showTrailingIcon={false}
          leadingIcon={<AppIcon name="arrow-left" size={14} />}
        >
          Voltar para o login
        </Button>
      </footer>
    )
  }

  return (
    <footer className="flex w-full flex-col items-center">
      <div className="my-3 flex w-full max-w-xs items-center gap-3 short:my-2.5">
        <span className="h-px flex-1 bg-gray-200" aria-hidden />
        {/* O slug `shield-check` apontava para um escudo liso; agora resolve
            para o desenho com o check ao centro. */}
        {/* `AppIcon` já renderiza em duotone por padrão. */}
        <AppIcon name="shield-check" size={20} className="shrink-0 text-brand" />
        <span className="h-px flex-1 bg-gray-200" aria-hidden />
      </div>

      <div className="flex w-full flex-col items-center gap-3 short:gap-2">
        {/* O texto de consentimento é o mesmo em toda a plataforma: vem do
            componente compartilhado, em vez de uma segunda cópia aqui. O
            `text-[11px]` anterior também ficava abaixo do piso de 12px. */}
        <TermsUseAndPolicies action={pageAction} />
        {pageAction === 'sign-up' ? (
          <AuthActionButton action="sign-up" onClick={() => goToSignIn()} />
        ) : (
          <AuthActionButton action="sign-in" onClick={() => goToSignUp()} />
        )}
      </div>
    </footer>
  )
}
